import { Button, ButtonGroup } from "@sikt/sds-button";
import { Heading1, Paragraph } from "@sikt/sds-core";
import { CheckboxInput } from "@sikt/sds-checkbox";
import { Fieldset } from "@sikt/sds-form";
import { AddIcon, SubtractIcon } from "@sikt/sds-icons";
import { Alert } from "@sikt/sds-message";
import { RadioFieldset, RadioInput } from "@sikt/sds-radio";
import clsx from "clsx";
import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import type { Ballot, VotePublic } from "../../shared/types";
import { NotFoundNotice } from "../components/NotFoundNotice";
import { ApiError, api } from "../lib/api";
import styles from "./VotePage.module.css";

type LoadState =
  | { phase: "loading" }
  | { phase: "notfound" }
  | { phase: "error"; message: string }
  | { phase: "ready"; vote: VotePublic };

function initialDots(vote: VotePublic, ballot: Ballot | null): number[] {
  if (
    ballot?.dots &&
    ballot.dots.length === vote.options.length &&
    ballot.dots.every((n) => Number.isInteger(n) && n >= 0)
  ) {
    return [...ballot.dots];
  }
  return vote.options.map(() => 0);
}

export function VotePage() {
  const { voteId } = useParams();
  const [state, setState] = useState<LoadState>({ phase: "loading" });
  const [selections, setSelections] = useState<number[]>([]);
  const [dots, setDots] = useState<number[]>([]);
  const [hadBallot, setHadBallot] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    if (!voteId) {
      setState({ phase: "notfound" });
      return;
    }
    api
      .getVote(voteId)
      .then(({ vote, myBallot }) => {
        if (!active) return;
        setSelections(
          myBallot?.selections?.filter(
            (i) => Number.isInteger(i) && i >= 0 && i < vote.options.length,
          ) ?? [],
        );
        setDots(initialDots(vote, myBallot));
        setHadBallot(myBallot !== null);
        setState({ phase: "ready", vote });
      })
      .catch((error: unknown) => {
        if (!active) return;
        if (error instanceof ApiError && error.status === 404) {
          setState({ phase: "notfound" });
        } else {
          setState({
            phase: "error",
            message:
              error instanceof ApiError ? error.message : "Noe gikk galt.",
          });
        }
      });
    return () => {
      active = false;
    };
  }, [voteId]);

  if (state.phase === "notfound") return <NotFoundNotice what="avstemningen" />;
  if (state.phase === "loading") {
    return (
      <div aria-live="polite">
        <Paragraph>Henter avstemningen …</Paragraph>
      </div>
    );
  }
  if (state.phase === "error") {
    return (
      <Alert variant="critical">
        <Paragraph>{state.message}</Paragraph>
      </Alert>
    );
  }

  const { vote } = state;
  const maxDots = vote.maxDots ?? 1;
  const usedDots = dots.reduce((sum, n) => sum + n, 0);
  const remaining = maxDots - usedDots;

  const toggleSelection = (index: number, checked: boolean) => {
    setSelections((prev) =>
      checked
        ? [...prev, index].sort((a, b) => a - b)
        : prev.filter((i) => i !== index),
    );
    setJustSaved(false);
  };

  const adjustDots = (index: number, delta: number) => {
    setDots((prev) => {
      if (delta > 0 && remaining <= 0) return prev;
      return prev.map((n, i) =>
        i === index ? Math.min(maxDots, Math.max(0, n + delta)) : n,
      );
    });
    setJustSaved(false);
  };

  const canSubmit =
    vote.mode === "dots" ? usedDots >= 1 : selections.length >= 1;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    setBusy(true);
    try {
      const ballot: Ballot =
        vote.mode === "dots" ? { dots } : { selections };
      await api.submitBallot(vote.voteId, ballot);
      setHadBallot(true);
      setJustSaved(true);
    } catch (error) {
      if (error instanceof ApiError) {
        setSubmitError(error.details?.[0]?.message ?? error.message);
      } else {
        setSubmitError("Noe gikk galt. Prøv igjen.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={styles.panel} onSubmit={handleSubmit} noValidate>
      <Heading1 size="l">{vote.title}</Heading1>

      {vote.mode === "single" && (
        <RadioFieldset
          legend="Velg ett alternativ"
          name="valg"
          value={selections[0]?.toString() ?? ""}
          onChange={(event) => {
            const index = Number.parseInt(event.target.value, 10);
            if (
              Number.isInteger(index) &&
              index >= 0 &&
              index < vote.options.length
            ) {
              setSelections([index]);
              setJustSaved(false);
            }
          }}
        >
          {vote.options.map((option, index) => (
            <RadioInput key={index} value={String(index)} label={option} />
          ))}
        </RadioFieldset>
      )}

      {vote.mode === "multiple" && (
        <Fieldset legend="Velg ett eller flere alternativer">
          <div className={styles.checkList}>
            {vote.options.map((option, index) => (
              <CheckboxInput
                key={index}
                label={option}
                checked={selections.includes(index)}
                onChange={(_, checked) => toggleSelection(index, checked)}
              />
            ))}
          </div>
        </Fieldset>
      )}

      {vote.mode === "dots" && (
        <Fieldset
          legend={`Fordel inntil ${maxDots} stemmer`}
          helpText="Du kan gi flere stemmer til samme alternativ."
        >
          <ul className={styles.dotList}>
            {vote.options.map((option, index) => {
              const count = dots[index] ?? 0;
              return (
                <li key={index} className={styles.dotRow}>
                  <span className={styles.dotLabel}>{option}</span>
                  <span className={styles.dotMarks} aria-hidden="true">
                    {"●".repeat(count)}
                  </span>
                  <span className={styles.stepper}>
                    <Button
                      type="button"
                      size="small"
                      icon={<SubtractIcon />}
                      iconVariant="only"
                      disabled={busy || count === 0}
                      onClick={() => adjustDots(index, -1)}
                    >
                      {`Færre stemmer på ${option}`}
                    </Button>
                    <span
                      className={clsx(
                        styles.dotCount,
                        "sds-typography--tabular-nums",
                      )}
                    >
                      {count}
                    </span>
                    <Button
                      type="button"
                      size="small"
                      icon={<AddIcon />}
                      iconVariant="only"
                      disabled={busy || remaining <= 0}
                      onClick={() => adjustDots(index, 1)}
                    >
                      {`Flere stemmer på ${option}`}
                    </Button>
                  </span>
                </li>
              );
            })}
          </ul>
          <div aria-live="polite" className={styles.dotStatus}>
            <Paragraph>
              {remaining === 0
                ? "Alle stemmene er fordelt."
                : `${remaining} av ${maxDots} stemmer igjen.`}
            </Paragraph>
          </div>
        </Fieldset>
      )}

      <ButtonGroup variant="left">
        <Button type="submit" variant="strong" disabled={busy || !canSubmit}>
          {busy
            ? "Sender …"
            : hadBallot
              ? "Oppdater svaret mitt"
              : "Send inn svar"}
        </Button>
      </ButtonGroup>

      {submitError && (
        <Alert variant="critical">
          <Paragraph>{submitError}</Paragraph>
        </Alert>
      )}
      {justSaved && (
        <Alert variant="success">
          <Paragraph>
            Svaret ditt er registrert. Du kan endre det fra denne nettleseren
            når som helst – det nyeste svaret gjelder.
          </Paragraph>
        </Alert>
      )}
    </form>
  );
}
